import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PriceDisplay } from 'dibs-buyer-price';
import { useIntl } from 'dibs-react-intl';
import { isDefaultLang } from 'dibs-intl/exports/languages';
import currencies from 'dibs-currency-helpers/exports/currencies';

import { PdpSharedFramingStatusLazy } from '../PdpSharedFramingStatus/PdpSharedFramingStatusLazy';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import { type PdpSharedPriceDisplay_item$key } from './__generated__/PdpSharedPriceDisplay_item.graphql';
import dibsCss from 'dibs-css';
import classNames from 'classnames';

const itemFragment = graphql`
    fragment PdpSharedPriceDisplay_item on Item {
        ...PdpSharedFramingStatus_item @include(if: $afterDisplay)
        skus {
            serviceId
            ...PriceDisplay_sku
                @include(if: $isMultiSku)
                @arguments(
                    hasSkuId: true
                    userId: $userId
                    isTrade: $isTrade
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                )
        }
        ...PriceDisplay_item
            @include(if: $afterDisplayOrBuaSsr)
            @arguments(
                userId: $userId
                isTrade: $isTrade
                priceBookName: $priceBookName
                userCountryCode: $userCountryCode
            )
    }
`;

type Props = {
    currency: string;
    item: PdpSharedPriceDisplay_item$key;
    selectedSkuId: string | null;
    isSaleUrgencyBannerVariant?: boolean;
};

export const PdpSharedPriceDisplay: FC<Props> = ({
    currency,
    item: itemRef,
    selectedSkuId,
    isSaleUrgencyBannerVariant,
}) => {
    const intl = useIntl();
    const item = useFragment(itemFragment, itemRef);
    const { isBot, isMobile } = useServerVarsContext();
    const selectedSku = item?.skus?.find(sku => sku?.serviceId === selectedSkuId) || null;
    const showBotPrice = isBot && isDefaultLang(intl.locale);

    return (
        <div className={classNames(dibsCss.wFull, { [dibsCss.block]: isMobile && showBotPrice })}>
            {(showBotPrice ? currencies : [currency]).map(curr => (
                <PriceDisplay
                    key={curr}
                    item={item}
                    useSvgIcon
                    displayVatLabel
                    sku={selectedSku}
                    currency={curr}
                    transaction={null}
                    appendElementToPrice={<PdpSharedFramingStatusLazy item={item} />}
                    isSaleUrgencyBannerVariant={isSaleUrgencyBannerVariant}
                />
            ))}
        </div>
    );
};
